import * as Sentry from '@sentry/vue';
import {
    BrowserTracing,
} from '@sentry/tracing';
import {
    createApp, watch,
} from 'vue';
import {
    createGtm,
} from '@gtm-support/vue-gtm';

import {
    VueSvgIconPlugin,
} from '@yzfe/vue3-svgicon';
import {
    createPinia,
    storeToRefs,
} from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import {
    makeGA4Tracker,
} from './utils/tracker-ga4';
import {
    registerTracker, trackVisibility,
} from './utils/tracking';
import {
    useAuth,
} from './stores/user-store';

import App from './components/App';
import config from './config';
import i18n from './utils/i18n';
import randomFieldId from './utils/random-field-id';
import router from './router';

import {
    hasSlotContent,
} from './utils/has-slot-content';

const pinia = createPinia();
pinia.use(piniaPluginPersistedState);
const app = createApp(App);

app.use(pinia);
app.use(router);

if (config.sentry) {
    Sentry.init({
        app,
        dsn: config.sentry.dsn,
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: [
                    'localhost',
                    'kcdn.at',
                    // This matches all requests relative to the current domain.
                    // E.g., `/backend/stuff`.
                    /^\//,
                    // Match all karriere.at origins except `gtm.karriere.at`.
                    // If you want to exclude other subdomains you can do it
                    // with a regular expression like this:
                    // `/^((?!gtm\.|other\.).)*karriere.at.*$/`
                    /^((?!gtm\.|login\.).)*karriere.at.*$/,
                ],
            }),
        ],
        tracesSampleRate: config.sentry.tracesSampleRate,
        trackComponents: true,
        hooks: ['activate', 'create', 'destroy', 'mount', 'update'],
        release: process.env.VUE_APP_RELEASE,
        environment: process.env.VUE_APP_ENV,
        allowUrls: [
            /.*\.karriere\.at/,
            /kcdn\.at/,
        ],
        ignoreErrors: [
            /Navigation cancelled from .*/,
            'NavigationDuplicated',
        ],
    });

    Sentry.setTag('version', process.env.VUE_APP_VERSION);
    Sentry.setTag('source', 'js');

    const auth = useAuth();
    const { uuid } = storeToRefs(auth);
    watch(uuid, () => {
        Sentry.setUser({ id: uuid.value });
    });
}

if (config.gtm) {
    app.use(createGtm({
        id: config.gtm.id,
        enabled: false,
        debug: config.gtm.debug,
        ignoredViews: ['page-cv-pdf-preview'],
        loadScript: true,
    }));
    registerTracker(makeGA4Tracker());
}

app.use(VueSvgIconPlugin, {
    tagName: 'AppSvgIcon',
    classPrefix: 'c-appIcon-',
});

app.config.globalProperties.$t = i18n;
app.config.globalProperties.$randomFieldId = randomFieldId;
app.config.globalProperties.$hasSlotContent = hasSlotContent;

app.directive('track-visibility', {
    mounted(el, { value = { element: null, elementDetail: null } }) {
        if (value.element) {
            trackVisibility(value);
        } else {
            trackVisibility({
                element: el.dataset.gtmElement ?? null,
                elementDetail: el.dataset.gtmElementDetail ?? null,
            });
        }
    },
});

app.mount('#app');
